import { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, RefreshCw, Trash2, Settings2 } from 'lucide-react';
import PeriodizationMatrix from './PeriodizationMatrix';
import BlockSessionSourcePanel from './BlockSessionSourcePanel';
import PeriodizationWeekSessionsModal from './PeriodizationWeekSessionsModal';
import { cn } from '../../lib/utils';
import AttributeValueEditor from './AttributeValueEditor';
import AttributeTrendsPanel from './AttributeTrendsPanel';
import RealignPlanModal from './RealignPlanModal';
import BlockPlanValuesPanel from './BlockPlanValuesPanel';
import BlockWeekAttributesTable from './BlockWeekAttributesTable';
import PlanningParametersModal from './PlanningParametersModal';
import PlanAdvancedSettingsModal from './PlanAdvancedSettingsModal';
import { mondayOfWeekYmd } from '../../utils/blockCalendarWeekUtils';
import { typesForPlanningScope } from '../../utils/periodizationPlanningScope';

const KIND_KEYS = { period: 'period', fixed_weeks: 'fixedWeeks', target_race: 'targetRace' };

const PHASE_CHIP = {
  base: 'bg-emerald-600 text-white',
  build: 'bg-primary text-primary-foreground',
  peak: 'bg-rose-600 text-white',
  taper: 'bg-sky-600 text-white',
};

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

export default function PeriodizationEditor({
  tree,
  onUpdated,
  compact = false,
  assessmentSets = [],
  onOpenAssessmentManager,
}) {
  const { t } = useLanguage();
  const [mainTab, setMainTab] = useState('plan');
  const [selectedPhaseId, setSelectedPhaseId] = useState(tree?.phases?.[0]?.id || null);
  const [saving, setSaving] = useState(false);
  const [scaffolding, setScaffolding] = useState(false);
  const [propagating, setPropagating] = useState(false);
  const [removingAllSessions, setRemovingAllSessions] = useState(false);
  const [regeneratingAllSessions, setRegeneratingAllSessions] = useState(false);
  const [realignOpen, setRealignOpen] = useState(false);
  const [paramsModalOpen, setParamsModalOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [movingMicroId, setMovingMicroId] = useState(null);
  const [sessionsOnly, setSessionsOnly] = useState(null);
  const [localAttrs, setLocalAttrs] = useState({});
  const [phaseDates, setPhaseDates] = useState({});

  const phase = tree?.phases?.find((p) => p.id === selectedPhaseId) || tree?.phases?.[0];
  const allTypes = tree?.assessment_set?.attribute_types || [];
  const scopeOverrides = tree?.block?.planning_scope_overrides;
  const phaseTypes = typesForPlanningScope(allTypes, scopeOverrides, 'phase');
  const attrs = { ...(phase?.attributes || {}), ...localAttrs };
  const datePatch = phaseDates[phase?.id] || {};

  const reloadBlock = useCallback(async () => {
    if (!tree?.block?.id) return;
    const res = await api.get(`/api/training/periodization/blocks/${tree.block.id}`);
    onUpdated?.(res.data);
  }, [tree?.block?.id, onUpdated]);

  const savePhase = useCallback(async () => {
    if (!phase?.id) return;
    setSaving(true);
    try {
      const body = {
        attributes: attrs,
        propagate_to_micros: false,
      };
      if (datePatch.start_date || datePatch.end_date) {
        if (datePatch.start_date) body.start_date = datePatch.start_date;
        if (datePatch.end_date) body.end_date = datePatch.end_date;
      }
      const res = await api.put(`/api/training/periodization/phases/${phase.id}`, body);
      setLocalAttrs({});
      setPhaseDates((prev) => ({ ...prev, [phase.id]: {} }));
      onUpdated?.(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, [phase?.id, attrs, datePatch, onUpdated]);

  const scaffold = async () => {
    if (!tree?.block?.id) return;
    setScaffolding(true);
    try {
      await api.post(`/api/training/periodization/blocks/${tree.block.id}/scaffold`);
      await reloadBlock();
    } catch (e) {
      console.error(e);
    } finally {
      setScaffolding(false);
    }
  };

  const propagate = async () => {
    if (!tree?.block?.id) return;
    setPropagating(true);
    try {
      await api.post(`/api/training/periodization/blocks/${tree.block.id}/propagate`);
      await reloadBlock();
    } catch (e) {
      console.error(e);
    } finally {
      setPropagating(false);
    }
  };

  const removeAllSessions = async () => {
    if (!tree?.block?.id) return;
    if (!window.confirm(t('periodization.removeAllSessionsConfirm'))) return;
    setRemovingAllSessions(true);
    try {
      const res = await api.post(
        `/api/training/periodization/blocks/${tree.block.id}/week-sessions/remove-all`
      );
      onUpdated?.(res.data.tree);
      const skipped = res.data.sessions_skipped_executed ?? 0;
      const calSkipped = res.data.calendar_entries_skipped_executed ?? 0;
      const weekRemoved = res.data.sessions_removed ?? 0;
      const calRemoved = res.data.calendar_entries_removed ?? 0;
      const parts = [];
      if (calRemoved > 0) parts.push(t('periodization.purgeCalendarDone', { count: calRemoved }));
      if (weekRemoved === 0 && calRemoved === 0) parts.push(t('periodization.removeAllSessionsNone'));
      if (skipped > 0 || calSkipped > 0) {
        parts.push(t('periodization.removeAllSessionsSkipped', { count: skipped + calSkipped }));
      }
      if (parts.length > 0) window.alert(parts.join('\n'));
      window.dispatchEvent(new Event('trainingUpdated'));
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.removeAllSessionsError'));
    } finally {
      setRemovingAllSessions(false);
    }
  };

  const regenerateAllSessions = async () => {
    if (!tree?.block?.id) return;
    if (!window.confirm(t('periodization.regenerateAllSessionsConfirm'))) return;
    setRegeneratingAllSessions(true);
    try {
      const res = await api.post(
        `/api/training/periodization/blocks/${tree.block.id}/week-sessions/regenerate-all`
      );
      onUpdated?.(res.data.tree);
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || t('periodization.regenerateAllSessionsError'));
    } finally {
      setRegeneratingAllSessions(false);
    }
  };

  const moveWeekToPhase = async (microId, phaseId) => {
    setMovingMicroId(microId);
    try {
      const res = await api.put(`/api/training/periodization/microcycles/${microId}`, {
        phase_id: phaseId,
      });
      onUpdated?.(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setMovingMicroId(null);
    }
  };

  const planLapsed = useMemo(() => {
    if (!tree?.block?.start_date || tree.block.microcycles_enabled === false) return null;
    const today = new Date().toISOString().slice(0, 10);
    const blockStartMonday = mondayOfWeekYmd(tree.block.start_date);
    const currentMonday = mondayOfWeekYmd(today);
    if (blockStartMonday >= currentMonday) return null;
    const weeks = Math.floor(
      (new Date(`${currentMonday}T12:00:00`) - new Date(`${blockStartMonday}T12:00:00`)) /
        (7 * 24 * 60 * 60 * 1000)
    );
    return weeks > 0 ? { weeks, start: blockStartMonday } : null;
  }, [tree?.block?.start_date, tree?.block?.microcycles_enabled]);

  if (!tree?.block) return null;

  const showMicroActions = tree.block.microcycles_enabled !== false;
  const kindKey = KIND_KEYS[tree.block.block_kind] || 'fixedWeeks';
  const blockEndDisplay =
    tree.block.block_kind === 'target_race' && tree.block.target_race_date
      ? tree.block.target_race_date
      : tree.block.end_date;

  const phaseStart = datePatch.start_date ?? phase?.start_date;
  const phaseEnd = datePatch.end_date ?? phase?.end_date;

  return (
    <div className={cn('flex flex-col gap-4', compact ? '' : 'min-h-0')}>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant='outline'>{tree.block.status}</Badge>
        <Badge variant='secondary' className='text-[10px]'>
          {t(`periodization.blockKinds.${kindKey}`)}
        </Badge>
        {tree.block.target_race_date && (
          <Badge variant='outline' className='text-[10px]'>
            {t('periodization.raceDate')}: {tree.block.target_race_date}
          </Badge>
        )}
        <span className='text-xs text-muted-foreground'>
          {tree.block.start_date} → {blockEndDisplay}
        </span>
        <div className='ml-auto flex flex-wrap gap-2'>
          {showMicroActions && (
            <>
              <Button type='button' size='sm' variant='secondary' onClick={() => setRealignOpen(true)}>
                {t('periodization.realignPlan.open')}
              </Button>
              <Button type='button' size='sm' variant='outline' onClick={scaffold} disabled={scaffolding}>
                {scaffolding ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.scaffoldWeeks')}
              </Button>
              <Button type='button' size='sm' variant='outline' onClick={propagate} disabled={propagating}>
                {propagating ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.propagate')}
              </Button>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='gap-1.5'
                onClick={regenerateAllSessions}
                disabled={regeneratingAllSessions || removingAllSessions}
              >
                {regeneratingAllSessions ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
                {t('periodization.regenerateAllSessions')}
              </Button>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='gap-1.5 text-destructive hover:text-destructive'
                onClick={removeAllSessions}
                disabled={removingAllSessions || regeneratingAllSessions}
              >
                {removingAllSessions ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Trash2 className='h-4 w-4' />
                )}
                {t('periodization.removeAllSessions')}
              </Button>
            </>
          )}
          <Button
            type='button'
            size='sm'
            variant='ghost'
            className='gap-1.5 text-muted-foreground'
            onClick={() => setAdvancedOpen(true)}
          >
            <Settings2 className='h-4 w-4' />
            {t('periodization.advanced.title')}
          </Button>
        </div>
      </div>

      {planLapsed ? (
        <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3'>
          <p className='text-sm text-foreground'>
            {t('periodization.realignPlan.banner', {
              weeks: planLapsed.weeks,
              start: planLapsed.start,
            })}
          </p>
          <Button type='button' size='sm' variant='outline' onClick={() => setRealignOpen(true)}>
            {t('periodization.realignPlan.open')}
          </Button>
        </div>
      ) : null}

      {!compact && (
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList>
            <TabsTrigger value='plan'>{t('periodization.tabs.plan')}</TabsTrigger>
            <TabsTrigger value='phases'>{t('periodization.tabs.phases')}</TabsTrigger>
          </TabsList>

          <TabsContent value='plan' className='mt-4 space-y-4'>
            {showMicroActions && (
              <div className='rounded-lg border border-border overflow-hidden'>
                <div className='border-b border-border bg-muted/30 px-3 py-2'>
                  <h3 className='text-sm font-semibold'>{t('periodization.tabs.planTimeline')}</h3>
                  <p className='text-[10px] text-muted-foreground'>{t('periodization.week.dragHint')}</p>
                </div>
                <PeriodizationMatrix
                  tree={tree}
                  mode='timeline'
                  draggableWeeks
                  onWeekClick={(micro, ph) => setSessionsOnly({ micro, phase: ph })}
                  onSessionsClick={(micro, ph) => setSessionsOnly({ micro, phase: ph })}
                  onWeekMove={moveWeekToPhase}
                />
                {movingMicroId && (
                  <p className='text-[10px] text-muted-foreground px-3 pb-2 flex items-center gap-1'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    {t('periodization.week.moving')}
                  </p>
                )}
              </div>
            )}

            <BlockPlanValuesPanel
              tree={tree}
              onUpdated={onUpdated}
              onConfigureParameters={() => setParamsModalOpen(true)}
            />

            {showMicroActions && (
              <div className='space-y-2'>
                <div>
                  <h3 className='text-sm font-semibold'>{t('periodization.weekAttributes.title')}</h3>
                  <p className='text-xs text-muted-foreground'>{t('periodization.weekAttributes.hint')}</p>
                </div>
                <BlockWeekAttributesTable tree={tree} onUpdated={reloadBlock} />
              </div>
            )}

            {showMicroActions && (
              <BlockSessionSourcePanel
                scope='block'
                scopeId={tree.block.id}
                initialConfig={tree.block.session_source_config}
                onChanged={reloadBlock}
              />
            )}

            <AttributeTrendsPanel tree={tree} types={allTypes.filter((ty) => ty.period_buckets?.week || ty.period_buckets?.phase)} />
          </TabsContent>

          <TabsContent value='phases' className='mt-4 space-y-4'>
            <div className='flex flex-wrap gap-2'>
              {tree.phases.map((p) => (
                <button
                  key={p.id}
                  type='button'
                  onClick={() => {
                    setSelectedPhaseId(p.id);
                    setLocalAttrs({});
                  }}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-opacity',
                    PHASE_CHIP[p.phase_kind] || 'bg-secondary text-secondary-foreground',
                    selectedPhaseId === p.id ? 'ring-2 ring-ring' : 'opacity-80 hover:opacity-100'
                  )}
                >
                  {p.name}
                </button>
              ))}
            </div>

            {phase && (
              <>
                <div className='grid gap-3 sm:grid-cols-2 max-w-md'>
                  <div>
                    <Label className='text-xs'>{t('periodization.phaseStart')}</Label>
                    <Input
                      type='date'
                      className='h-8 text-xs mt-1'
                      value={phaseStart || ''}
                      onChange={(e) =>
                        setPhaseDates((prev) => ({
                          ...prev,
                          [phase.id]: { ...prev[phase.id], start_date: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label className='text-xs'>{t('periodization.phaseEnd')}</Label>
                    <Input
                      type='date'
                      className='h-8 text-xs mt-1'
                      value={phaseEnd || ''}
                      onChange={(e) =>
                        setPhaseDates((prev) => ({
                          ...prev,
                          [phase.id]: { ...prev[phase.id], end_date: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>

                <div className='space-y-3 rounded-lg border border-border p-4'>
                  <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div>
                      <h3 className='text-sm font-semibold'>{t('periodization.planScope.phaseValuesTitle')}</h3>
                      <p className='text-xs text-muted-foreground'>{t('periodization.planScope.phaseValuesHint')}</p>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      <Button type='button' size='sm' variant='outline' onClick={() => setParamsModalOpen(true)}>
                        {t('periodization.planScope.configureParameters')}
                      </Button>
                      <Button type='button' size='sm' onClick={savePhase} disabled={saving}>
                        {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.savePhase')}
                      </Button>
                    </div>
                  </div>

                  {phaseTypes.length > 0 ? (
                    <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                      {phaseTypes.map((type) => (
                        <div key={type.type_key} className='rounded-lg border border-border p-3 space-y-2'>
                          <Label className='text-xs'>{attrLabel(t, type.type_key, type.name)}</Label>
                          <AttributeValueEditor
                            type={type}
                            value={attrs[type.type_key]}
                            onChange={(v) => setLocalAttrs((prev) => ({ ...prev, [type.type_key]: v }))}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='text-xs text-muted-foreground'>{t('periodization.planScope.noPhaseParams')}</p>
                  )}
                </div>

                {showMicroActions && (
                  <BlockSessionSourcePanel
                    scope='phase'
                    scopeId={phase.id}
                    initialConfig={phase.session_source_config}
                    inheritedLabel={t('periodization.sessionSource.inherited')}
                  />
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      )}

      <PeriodizationWeekSessionsModal
        open={!!sessionsOnly}
        onOpenChange={(open) => !open && setSessionsOnly(null)}
        micro={sessionsOnly?.micro}
        phase={sessionsOnly?.phase}
        blockId={tree.block.id}
        onUpdated={reloadBlock}
      />

      <PlanningParametersModal
        open={paramsModalOpen}
        onOpenChange={setParamsModalOpen}
        tree={tree}
        onUpdated={onUpdated}
      />

      <PlanAdvancedSettingsModal
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        block={tree.block}
        assessmentSets={assessmentSets}
        onUpdated={onUpdated}
        onOpenAssessmentManager={onOpenAssessmentManager}
      />

      <RealignPlanModal
        open={realignOpen}
        onOpenChange={setRealignOpen}
        block={tree.block}
        onRealigned={(updatedTree) => {
          onUpdated?.(updatedTree);
          window.dispatchEvent(new Event('trainingUpdated'));
        }}
      />
    </div>
  );
}
